import { createContext, useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import useLangNavigate from "../hooks/useLangNavigate";
import { useAuth } from "../hooks/useAuth";
import { isDemoMode } from "../utils/demoApiInterceptor";
import { TOUR_SECTIONS } from "../components/tour/TourSteps";

const STORAGE_PREFIX = "offood_tour_";

const getStorageKey = (userId, restaurantId) =>
  `${STORAGE_PREFIX}${userId}_${restaurantId}`;

const loadTourState = (userId, restaurantId) => {
  if (!userId || !restaurantId) return null;
  try {
    const raw = localStorage.getItem(getStorageKey(userId, restaurantId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveTourState = (userId, restaurantId, state) => {
  if (!userId || !restaurantId) return;
  try {
    localStorage.setItem(
      getStorageKey(userId, restaurantId),
      JSON.stringify(state),
    );
  } catch {
    // silent fail
  }
};

const BANKING_SECTION_INDEX = TOUR_SECTIONS.findIndex(
  (section) => section.id === "banking",
);

const TourContext = createContext({
  isActive: false,
  currentSection: 0,
  currentStep: 0,
  completedSections: [],
  dismissed: false,
  singleSectionMode: false,
  isTooltipVisible: true,
  restartTick: 0,
  stepData: null,
  totalStepsInSection: 0,
  startTour: () => {},
  startSection: () => {},
  goToSection: () => {},
  restartStep: () => {},
  hideTooltip: () => {},
  showTooltip: () => {},
  nextStep: () => {},
  prevStep: () => {},
  skipSection: () => {},
  skipTour: () => {},
  endTour: () => {},
  registerBeforeSectionComplete: () => () => {},
  registerSectionGuard: () => () => {},
});

const isPathRestaurantOverview = (pathname) => {
  const normalized = pathname.replace(/\/$/, "") || "/";
  return (
    /\/dashboard$/.test(normalized) &&
    !normalized.includes("/company/dashboard")
  );
};

export const TourProvider = ({ children, isStripeDone = null }) => {
  const navigate = useLangNavigate();
  const { pathname } = useLocation();
  const { isPureRestaurantAdmin } = useAuth();
  const userId = useSelector((state) => state.user.userId);
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const isControllingEntity = useSelector(
    (state) => state.admin.isControllingEntity,
  );

  const [isActive, setIsActive] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSections, setCompletedSections] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [singleSectionMode, setSingleSectionMode] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(true);
  const [restartTick, setRestartTick] = useState(0);
  const beforeSectionCompleteHandlersRef = useRef({});
  const isAdvancingRef = useRef(false);

  const navigatingRef = useRef(false);
  const autoStartTriggered = useRef(false);

  // Load persisted state on mount or when user/restaurant changes
  useEffect(() => {
    const saved = loadTourState(userId, restaurantId);
    if (saved) {
      setCompletedSections(saved.completedSections || []);
      setDismissed(saved.dismissed || false);
    } else {
      setCompletedSections([]);
      setDismissed(false);
    }
    setIsActive(false);
    setIsTooltipVisible(true);
    setRestartTick(0);
    setCurrentSection(0);
    setCurrentStep(0);
    beforeSectionCompleteHandlersRef.current = {};
    autoStartTriggered.current = false;
  }, [userId, restaurantId]);

  const persist = useCallback(
    (overrides = {}) => {
      const state = {
        completedSections,
        dismissed,
        ...overrides,
      };
      saveTourState(userId, restaurantId, state);
    },
    [userId, restaurantId, completedSections, dismissed],
  );

  const getCurrentStepData = useCallback(() => {
    const section = TOUR_SECTIONS[currentSection];
    if (!section) return null;
    return section.steps[currentStep] || null;
  }, [currentSection, currentStep]);

  const getTotalStepsInSection = useCallback(() => {
    const section = TOUR_SECTIONS[currentSection];
    return section ? section.steps.length : 0;
  }, [currentSection]);

  const navigateToStep = useCallback(
    (sectionIndex, stepIndex) => {
      const section = TOUR_SECTIONS[sectionIndex];
      if (!section) return;
      const step = section.steps[stepIndex];
      if (!step) return;

      const route = step.route || section.route;
      // Overview targets only exist on RestaurantOverview; ensure we are on /dashboard first.
      if (section.id === "overview") {
        if (isPathRestaurantOverview(pathname)) {
          setCurrentSection(sectionIndex);
          setCurrentStep(stepIndex);
          return;
        }
        navigatingRef.current = true;
        navigate(isDemoMode() ? "/demo/dashboard" : "/dashboard");
        setTimeout(() => {
          navigatingRef.current = false;
          setCurrentSection(sectionIndex);
          setCurrentStep(stepIndex);
        }, 400);
        return;
      }
      if (route) {
        navigatingRef.current = true;
        navigate(`/dashboard/${route}`);
        setTimeout(() => {
          navigatingRef.current = false;
          setCurrentSection(sectionIndex);
          setCurrentStep(stepIndex);
        }, 400);
      } else {
        setCurrentSection(sectionIndex);
        setCurrentStep(stepIndex);
      }
    },
    [navigate, pathname],
  );

  const startTour = useCallback(
    ({ sectionsToSkip = [], resetProgress = false } = {}) => {
      if (!isPureRestaurantAdmin) return;
      setSingleSectionMode(false);
      setDismissed(false);
      setIsTooltipVisible(true);

      const baseCompleted = resetProgress ? [] : completedSections;
      const merged = [...new Set([...baseCompleted, ...sectionsToSkip])];
      setCompletedSections(merged);
      persist({ completedSections: merged, dismissed: false });

      const firstIncomplete = TOUR_SECTIONS.findIndex(
        (_, i) => !merged.includes(i),
      );
      const startIdx = firstIncomplete >= 0 ? firstIncomplete : 0;

      setIsActive(true);
      navigateToStep(startIdx, 0);
    },
    [completedSections, isPureRestaurantAdmin, navigateToStep, persist],
  );

  const startSection = useCallback(
    (sectionIndex) => {
      if (!isPureRestaurantAdmin) return;
      if (sectionIndex < 0 || sectionIndex >= TOUR_SECTIONS.length) return;
      setSingleSectionMode(true);
      setIsTooltipVisible(true);
      setIsActive(true);
      navigateToStep(sectionIndex, 0);
    },
    [isPureRestaurantAdmin, navigateToStep],
  );

  const goToSection = useCallback(
    (sectionIndex) => {
      if (!isPureRestaurantAdmin) return;
      if (navigatingRef.current) return;
      if (sectionIndex < 0 || sectionIndex >= TOUR_SECTIONS.length) return;
      setSingleSectionMode(false);
      setIsTooltipVisible(true);
      setIsActive(true);
      navigateToStep(sectionIndex, 0);
    },
    [isPureRestaurantAdmin, navigateToStep],
  );

  const markSectionComplete = useCallback(
    (sectionIndex) => {
      const updated = completedSections.includes(sectionIndex)
        ? completedSections
        : [...completedSections, sectionIndex];
      setCompletedSections(updated);
      persist({ completedSections: updated });
      return updated;
    },
    [completedSections, persist],
  );

  const registerBeforeSectionComplete = useCallback((sectionId, handler) => {
    if (!sectionId || typeof handler !== "function") return () => {};
    beforeSectionCompleteHandlersRef.current[sectionId] = handler;
    return () => {
      if (beforeSectionCompleteHandlersRef.current[sectionId] === handler) {
        delete beforeSectionCompleteHandlersRef.current[sectionId];
      }
    };
  }, []);

  const registerSectionGuard = useCallback((sectionId, handler) => {
    return registerBeforeSectionComplete(sectionId, handler);
  }, [registerBeforeSectionComplete]);

  const runSectionGuard = useCallback(
    async (section, context) => {
      if (!section?.id) return true;
      const sectionHandler = beforeSectionCompleteHandlersRef.current[section.id];
      if (!sectionHandler) return true;
      try {
        const shouldContinue = await Promise.resolve(sectionHandler(context));
        return shouldContinue !== false;
      } catch {
        return false;
      }
    },
    []
  );

  const nextStep = useCallback(async () => {
    if (navigatingRef.current || isAdvancingRef.current) return;
    isAdvancingRef.current = true;

    const section = TOUR_SECTIONS[currentSection];
    if (!section) {
      isAdvancingRef.current = false;
      return;
    }

    if (currentStep < section.steps.length - 1) {
      const nextIdx = currentStep + 1;
      const nextStepData = section.steps[nextIdx];
      const currentRoute = section.steps[currentStep]?.route || section.route;
      const nextRoute = nextStepData?.route || section.route;

      const canProceed = await runSectionGuard(section, {
        reason: "step-transition",
        currentSection,
        currentStep,
        nextStep: nextIdx,
        currentRoute,
        nextRoute,
      });
      if (!canProceed) {
        isAdvancingRef.current = false;
        return;
      }

      if (nextRoute && nextRoute !== currentRoute) {
        navigateToStep(currentSection, nextIdx);
      } else {
        setCurrentStep(nextIdx);
      }
      isAdvancingRef.current = false;
    } else {
      const canProceed = await runSectionGuard(section, {
        reason: "section-complete",
        currentSection,
        currentStep,
        nextStep: null,
        currentRoute: section.steps[currentStep]?.route || section.route,
        nextRoute: null,
      });
      if (!canProceed) {
        isAdvancingRef.current = false;
        return;
      }

      const updated = markSectionComplete(currentSection);

      if (singleSectionMode) {
        setIsActive(false);
        setSingleSectionMode(false);
        isAdvancingRef.current = false;
        return;
      }

      let nextSection = -1;
      for (let i = currentSection + 1; i < TOUR_SECTIONS.length; i++) {
        if (!updated.includes(i)) {
          nextSection = i;
          break;
        }
      }

      if (nextSection >= 0) {
        navigateToStep(nextSection, 0);
      } else {
        setIsActive(false);
        const allComplete = TOUR_SECTIONS.map((_, i) => i);
        setCompletedSections(allComplete);
        persist({ completedSections: allComplete });
      }
      isAdvancingRef.current = false;
    }
  }, [
    currentSection,
    currentStep,
    singleSectionMode,
    markSectionComplete,
    navigateToStep,
    persist,
    runSectionGuard,
  ]);

  const prevStep = useCallback(() => {
    if (navigatingRef.current) return;
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const skipSection = useCallback(() => {
    if (navigatingRef.current) return;

    const updated = markSectionComplete(currentSection);

    if (singleSectionMode) {
      setIsActive(false);
      setSingleSectionMode(false);
      return;
    }

    let nextSection = -1;
    for (let i = currentSection + 1; i < TOUR_SECTIONS.length; i++) {
      if (!updated.includes(i)) {
        nextSection = i;
        break;
      }
    }

    if (nextSection >= 0) {
      navigateToStep(nextSection, 0);
    } else {
      setIsActive(false);
      const allComplete = TOUR_SECTIONS.map((_, i) => i);
      setCompletedSections(allComplete);
      persist({ completedSections: allComplete });
    }
  }, [
    currentSection,
    singleSectionMode,
    markSectionComplete,
    navigateToStep,
    persist,
  ]);

  const skipTour = useCallback(() => {
    setIsActive(false);
    setDismissed(true);
    setSingleSectionMode(false);
    setIsTooltipVisible(true);
    persist({ dismissed: true });
  }, [persist]);

  const endTour = useCallback(() => {
    setIsActive(false);
    setSingleSectionMode(false);
    setIsTooltipVisible(true);
  }, []);

  const restartStep = useCallback(() => {
    if (!isActive || navigatingRef.current) return;
    setIsTooltipVisible(true);
    setRestartTick((tick) => tick + 1);
  }, [isActive]);

  const hideTooltip = useCallback(() => {
    if (!isActive) return;
    setIsTooltipVisible(false);
  }, [isActive]);

  const showTooltip = useCallback(() => {
    if (!isActive) return;
    setIsTooltipVisible(true);
  }, [isActive]);

  const allSectionsComplete =
    completedSections.length >= TOUR_SECTIONS.length;

  // Auto-start: trigger unless user dismissed OR all sections complete
  useEffect(() => {
    if (autoStartTriggered.current || !userId || !restaurantId) return;
    if (!isPureRestaurantAdmin) return;
    if (isControllingEntity) return;
    if (isStripeDone === null) return;

    const saved = loadTourState(userId, restaurantId);
    const wasDismissed = saved?.dismissed === true;
    const savedCompleted = saved?.completedSections || [];
    const isAllComplete = savedCompleted.length >= TOUR_SECTIONS.length;

    if (wasDismissed || isAllComplete) return;

    autoStartTriggered.current = true;

    const sectionsToSkip =
      isStripeDone && BANKING_SECTION_INDEX >= 0 ? [BANKING_SECTION_INDEX] : [];

    const timer = setTimeout(
      () => startTour({ sectionsToSkip, resetProgress: false }),
      800,
    );
    return () => clearTimeout(timer);
  }, [
    userId,
    restaurantId,
    isPureRestaurantAdmin,
    isStripeDone,
    isControllingEntity,
    startTour,
  ]);

  return (
    <TourContext.Provider
      value={{
        isActive,
        currentSection,
        currentStep,
        completedSections,
        dismissed,
        singleSectionMode,
        isTooltipVisible,
        restartTick,
        allSectionsComplete,
        stepData: getCurrentStepData(),
        totalStepsInSection: getTotalStepsInSection(),
        sectionCount: TOUR_SECTIONS.length,
        sections: TOUR_SECTIONS,
        startTour,
        startSection,
        goToSection,
        restartStep,
        hideTooltip,
        showTooltip,
        nextStep,
        prevStep,
        skipSection,
        skipTour,
        endTour,
        registerBeforeSectionComplete,
        registerSectionGuard,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export default TourContext;
